// Copied from apps/admin/src/features/map-view/model/constants/truck-icons.ts (PRD #875).
import React from "react";
import { BoundedCache } from "../bounded-cache";
import { renderToString } from "react-dom/server";
import {
  IcTruckEnrouteBunker,
  IcTruckEnrouteBunkerSelected,
  IcTruckService,
  IcTruckServiceSelected,
  IcTruckEnrouteFree,
  IcTruckEnrouteFreeSelected,
  IcTruckEnrouteFull,
  IcTruckEnrouteFullSelected,
  IcTruckEnrouteSamosval,
  IcTruckEnrouteSamosvalFull,
  IcTruckEnrouteSamosvalFullSelected,
  IcTruckEnrouteSamosvalSelected,
  IcTruckSamosvalService,
  IcTruckSamosvalServiceSelected,
  IcTruckEnrouteMsw,
  IcTruckEnrouteMswSelected,
  IcTruckLiquidSelected,
  IcTruckLiquid,
} from "@racoon/icons";

/**
 * Map truck icon components to their respective functions
 * These are React components that can be rendered to HTML strings for Leaflet
 */
export const TRUCK_ICON_COMPONENTS = {
  icTruckEnrouteBunker: IcTruckEnrouteBunker,
  icTruckEnrouteBunkerSelected: IcTruckEnrouteBunkerSelected,
  icTruckService: IcTruckService,
  icTruckServiceSelected: IcTruckServiceSelected,
  icTruckEnrouteFree: IcTruckEnrouteFree,
  icTruckEnrouteFreeSelected: IcTruckEnrouteFreeSelected,
  icTruckEnrouteFull: IcTruckEnrouteFull,
  icTruckEnrouteFullSelected: IcTruckEnrouteFullSelected,
  icTruckEnrouteSamosval: IcTruckEnrouteSamosval,
  icTruckEnrouteSamosvalSelected: IcTruckEnrouteSamosvalSelected,
  icTruckEnrouteSamosvalFull: IcTruckEnrouteSamosvalFull,
  icTruckEnrouteSamosvalFullSelected: IcTruckEnrouteSamosvalFullSelected,
  icTruckSamosvalService: IcTruckSamosvalService,
  icTruckSamosvalServiceSelected: IcTruckSamosvalServiceSelected,
  icTruckEnrouteMsw: IcTruckEnrouteMsw,
  icTruckEnrouteMswSelected: IcTruckEnrouteMswSelected,
  icTruckLiquid: IcTruckLiquid,
  icTruckLiquidSelected: IcTruckLiquidSelected,
} as const;

/**
 * Convert a React component to an HTML string for use in Leaflet markers
 * @param Component - React component function (can be memoized or forward ref)
 * @returns HTML string representation of the component
 */
const renderIconComponent = (Component: React.ComponentType<Record<string, unknown>>): string => {
  if (!Component) {
    console.error("renderIconComponent received undefined component");
    return "";
  }
  try {
    return renderToString(React.createElement(Component));
  } catch (error) {
    console.error("Error rendering icon component:", error, Component);
    return "";
  }
};

/**
 * Get the HTML string for a truck icon component
 * @param componentName - Key from TRUCK_ICON_COMPONENTS
 * @returns HTML string for the icon
 */
/** Each component renders to a fixed string, and there are 19 of them. */
const truckHtmlCache = new BoundedCache<string>(512);

export const getTruckIconHTML = (componentName: keyof typeof TRUCK_ICON_COMPONENTS): string => {
  const cached = truckHtmlCache.get(componentName);
  if (cached !== undefined) return cached;

  const Component = TRUCK_ICON_COMPONENTS[componentName];
  if (!Component) {
    console.error(`getTruckIconHTML: Component "${componentName}" is undefined!`);
    console.error("Available components:", Object.keys(TRUCK_ICON_COMPONENTS));
  }
  const html = renderIconComponent(Component);
  truckHtmlCache.set(componentName, html);
  return html;
};
